<template>
  <li class="flex flex-col gap-3 rounded-md border p-3">
    <div v-if="!isEditing" class="flex items-center gap-2">
      <Checkbox
        :id="`checklist-item-${item.id}`"
        :model-value="item.completed"
        :disabled="readonly || isNotExecutable || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        @update:model-value="handleCompletionToggle"
      />

      <Button
        variant="ghost"
        class="h-9 flex-1 justify-start overflow-hidden border border-transparent px-3 hover:border-border hover:bg-muted/30"
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        @click="startEdit"
      >
        <span class="truncate font-medium" :class="item.completed ? 'line-through text-muted-foreground' : 'text-foreground'">
          {{ instructionLabel }}
        </span>
      </Button>

      <Button
        type="button"
        size="icon-sm"
        variant="outline"
        class="checklist-item-drag-handle shrink-0 cursor-grab text-muted-foreground shadow-none hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed"
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        aria-label="Drag to reorder checklist item"
      >
        <GripVertical class="h-4 w-4" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button aria-label="Checklist item actions" size="icon-sm" variant="ghost" :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting">
            <MoreHorizontal class="rotate-90" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-44">
          <DropdownMenuItem :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting" @click="emit('edit-notes', item)">
            {{ hasNotes ? 'Edit Notes' : 'Add Notes' }}
          </DropdownMenuItem>
          <DropdownMenuItem :disabled="readonly || isUpdatingCompletion || isSaving || isSorting || isDeleting" class="text-destructive" @click="emit('delete', item)">
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <div v-else class="flex items-center gap-2">
      <Checkbox
        :id="`checklist-item-${item.id}`"
        :model-value="item.completed"
        :disabled="readonly || isNotExecutable || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        @update:model-value="handleCompletionToggle"
      />

      <Input
        :id="instructionInputId"
        v-model="editableInstruction"
        class="flex-1"
        placeholder="Describe the required action..."
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        @keydown.enter.prevent="saveChecklistItem"
        @keydown.esc.prevent="cancelEdit"
      />

      <Button size="sm" :loading="isSaving" :disabled="!canSave || readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting" @click="saveChecklistItem"> Save </Button>

      <Button size="sm" variant="outline" :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting" @click="cancelEdit"> Cancel </Button>

      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button aria-label="Checklist item actions" size="icon-sm" variant="ghost" :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting">
            <MoreHorizontal class="rotate-90" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-44">
          <DropdownMenuItem :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting" @click="emit('edit-notes', item)">
            {{ hasNotes ? 'Edit Notes' : 'Add Notes' }}
          </DropdownMenuItem>
          <DropdownMenuItem :disabled="readonly || isUpdatingCompletion || isSaving || isSorting || isDeleting" class="text-destructive" @click="emit('delete', item)">
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <p v-if="!isEditing && completedStatusLabel" class="pl-10 text-xs text-muted-foreground">
      {{ completedStatusLabel }}
    </p>

    <p v-if="!isEditing && hasDescription" class="whitespace-pre-wrap pl-10 text-xs text-muted-foreground">
      {{ normalizedCurrentDetails }}
    </p>

    <div v-if="!isEditing && hasNotes" class="flex items-start gap-2 rounded-md border-l-2 border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        class="mt-[-0.125rem] h-5 w-5 shrink-0 rounded-sm text-muted-foreground hover:bg-transparent hover:text-foreground"
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        aria-label="Edit checklist item notes"
        @click="emit('edit-notes', item)"
      >
        <FileText class="h-3.5 w-3.5" />
      </Button>
      <p class="whitespace-pre-wrap">
        {{ normalizedCurrentNotes }}
      </p>
    </div>

    <div v-if="isEditing" class="flex items-start justify-between gap-3">
      <Button
        type="button"
        variant="link"
        size="sm"
        class="h-auto w-fit px-0 text-xs underline underline-offset-2 hover:text-primary/80"
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
        @click="toggleDescription"
      >
        {{ descriptionActionLabel }}
      </Button>
    </div>

    <div v-if="isEditing && isDescriptionVisible" class="flex flex-col gap-1.5">
      <label class="text-xs font-medium text-muted-foreground">Description</label>
      <textarea
        v-model="editableDetails"
        rows="3"
        class="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none transition focus-visible:ring-1 focus-visible:ring-ring"
        placeholder="Add checklist item description..."
        :disabled="readonly || isUpdatingCompletion || isSaving || isDeleting || isSorting"
      />
    </div>

    <p v-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>
  </li>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { FileText, GripVertical, MoreHorizontal } from 'lucide-vue-next'
import { Button } from '@/components/ui/button/index.js'
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu/index.js'
import { Input } from '@/components/ui/input/index.js'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isUpdatingCompletion: {
    type: Boolean,
    default: false,
  },
  isNotExecutable: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  isSorting: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['toggle-completion', 'save', 'edit-notes', 'delete'])

const editableInstruction = ref('')
const editableDetails = ref('')
const isDescriptionVisible = ref(false)
const isEditing = ref(false)
const instructionInputId = computed(() => `checklist-item-instruction-${props.item.id}`)

const normalizedCurrentInstruction = computed(() => {
  return String(props.item.instruction ?? '').trim()
})

const normalizedCurrentDetails = computed(() => {
  return String(props.item.details ?? '').trim()
})

const normalizedEditableInstruction = computed(() => {
  return String(editableInstruction.value ?? '').trim()
})

const normalizedEditableDetails = computed(() => {
  return String(editableDetails.value ?? '').trim()
})

const normalizedCurrentNotes = computed(() => {
  return String(props.item?.notes ?? '').trim()
})

const hasNotes = computed(() => {
  return normalizedCurrentNotes.value.length > 0
})

const hasDescription = computed(() => {
  return normalizedCurrentDetails.value.length > 0
})

const instructionLabel = computed(() => {
  if (normalizedCurrentInstruction.value.length > 0) {
    return normalizedCurrentInstruction.value
  }

  return `Checklist item #${props.item.id}`
})

const completedStatusLabel = computed(() => {
  if (!props.item.completed) {
    return null
  }

  const completedBy = props.item.completed_by_user?.name ? ` by ${props.item.completed_by_user.name}` : ''

  if (!props.item.completed_at) {
    return `Completed${completedBy}`
  }

  const completedAtDate = new Date(props.item.completed_at)
  const completedAtLabel = Number.isNaN(completedAtDate.getTime()) ? '' : ` on ${completedAtDate.toLocaleString()}`

  return `Completed${completedBy}${completedAtLabel}`
})

const descriptionActionLabel = computed(() => {
  if (isDescriptionVisible.value) {
    return 'Hide description'
  }

  return hasDescription.value ? 'Edit description' : 'Add a description'
})

const canSave = computed(() => {
  if (normalizedEditableInstruction.value.length === 0) {
    return false
  }

  return normalizedEditableInstruction.value !== normalizedCurrentInstruction.value || normalizedEditableDetails.value !== normalizedCurrentDetails.value
})

const synchronizeFormState = () => {
  editableInstruction.value = props.item.instruction ?? ''
  editableDetails.value = props.item.details ?? ''
}

const toggleDescription = () => {
  if (props.readonly) {
    return
  }

  isDescriptionVisible.value = !isDescriptionVisible.value
}

const handleCompletionToggle = (value) => {
  if (props.readonly || props.isNotExecutable || props.isUpdatingCompletion || props.isSaving || props.isDeleting || props.isSorting) {
    return
  }

  emit('toggle-completion', { item: props.item, value: value === true })
}

const focusInstructionInput = async () => {
  await nextTick()

  const nativeInput = document.getElementById(instructionInputId.value)
  if (!nativeInput) {
    return
  }

  nativeInput.focus()
}

const startEdit = async () => {
  if (props.readonly || props.isUpdatingCompletion || props.isSaving || props.isDeleting || props.isSorting) {
    return
  }

  isEditing.value = true
  isDescriptionVisible.value = hasDescription.value
  await focusInstructionInput()
}

const cancelEdit = () => {
  if (props.readonly || props.isUpdatingCompletion || props.isSaving || props.isDeleting || props.isSorting) {
    return
  }

  synchronizeFormState()
  isDescriptionVisible.value = false
  isEditing.value = false
}

const saveChecklistItem = () => {
  if (!canSave.value || props.readonly || props.isUpdatingCompletion || props.isSaving || props.isDeleting || props.isSorting) {
    return
  }

  emit('save', {
    item: props.item,
    instruction: editableInstruction.value,
    details: editableDetails.value,
  })

  isDescriptionVisible.value = false
  isEditing.value = false
}

watch(
  () => props.item,
  () => {
    synchronizeFormState()
  },
  { immediate: true, deep: true },
)

watch(
  () => props.errorMessage,
  (errorMessage) => {
    if (errorMessage) {
      isEditing.value = true
      isDescriptionVisible.value = true
    }
  },
)
</script>
